import React, { useState, useEffect, useCallback } from 'react';
import { getRecognitions } from '../services/api';
import RecognitionCard from '../components/RecognitionCard';
import RecognitionModal from '../components/RecognitionModal';
import ActivitySidebar from '../components/ActivitySidebar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Sparkles, TrendingUp, Clock, Loader2, Users, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';

const FUNDAMENTALS = [
  { id: 1, name: 'Do the right thing, always', nameChinese: '始终，为所应为',
    desc: 'Demonstrate an unwavering commitment to doing the right thing in every action you take and in every decision you make, especially when no one\'s looking. Always tell the truth, no matter the consequences. If you make a mistake, own up to it, apologize, and make it right.',
    descChinese: '在你的每个行动以及做出的每项决定中，表现出坚定不移的承诺，尤其是无人监管时。无论后果轻重，始终保持诚实。如果犯错，要勇于承认并道歉和改正。' },
  { id: 2, name: 'Be a fanatic about response time', nameChinese: '做一名响应迅速的狂热分子',
    desc: 'Respond to questions and concerns quickly, whether it\'s in person, on the phone, or by e-mail. This includes simply acknowledging that we got the question and we\'re "on it," as well as keeping those involved continuously updated on the status of outstanding issues.',
    descChinese: '快速响应问题或疑虑，无论是当面，还是通过电话或电子邮件。这既包括表明我们收到了问题并"着手"解决，也包括不断向相关方通报未决问题的进展。' },
  { id: 3, name: 'Demonstrate a design mentality', nameChinese: '体现出设计思维',
    desc: 'Start with "why?" Why are we doing this? What are the goals? Who will be impacted? What\'s the most effective process? Be intentional about everything you do.',
    descChinese: '以"为什么?"为起点。为什么做这件事？目标是什么？谁会受到影响？最有成效的流程是什么？做任何事情都要有明确的目的。' },
  { id: 4, name: 'Think globally', nameChinese: '具备国际思维',
    desc: 'We serve internal and external customers and consumers across the globe. As you make decisions, remember to consider various time zones, cultures, norms, logistics, and perspectives.',
    descChinese: '我们为内部和外部客户以及全球各地的消费者提供服务。做决定时，切记考虑不同时区、文化、规范、逻辑和视角。' },
  { id: 5, name: 'Be proud of your work', nameChinese: '为自己的工作感到自豪',
    desc: 'Demonstrate a passion for excellence and take pride in the quality of everything you touch and everything you do. Have a healthy disdain for mediocrity. Good is not good enough. Always ask yourself, "Is this my best work?"',
    descChinese: '充满热情追求最佳水准，凡是你接触的或是你负责的事，都应该发挥出使你骄傲的最佳水准。不应满足于平庸。没有最好，只有更好。经常自问："这是我的最佳水准吗？"' },
  { id: 6, name: 'Honor commitments', nameChinese: '信守承诺',
    desc: 'Be reliable and do what you say you\'re going to do, when you say you\'re going to do it. This includes being on time for all phone calls, appointments, meetings, and promises. If a commitment can\'t be fulfilled, notify others early and agree on a new deliverable to be honored.',
    descChinese: '为人可靠，信守承诺。这包括及时回复电话、赴约、开会和兑现诺言。如果无法履行诺言，尽早通知对方，并约定新的兑现事项。' },
  { id: 7, name: 'Practice blameless problem-solving', nameChinese: '解决问题，不指责',
    desc: 'Demonstrate a relentless solution focus, rather than pointing fingers or dwelling on problems. Identify lessons learned and use those lessons to improve ourselves and our processes so we don\'t make the same mistake twice. Get smarter with every mistake. Learn from every experience.',
    descChinese: '坚持不懈地以解决问题为重点，而非横加指责或停留在问题上。总结教训，并以此改进自己和流程，以免重复犯错。吃一堑长一智。从每次经验中学习。' },
  { id: 8, name: 'Make a difference', nameChinese: '做出贡献',
    desc: 'Have a generous spirit. Contribute your time, effort, and where appropriate, your money, to make your community and the lives of others better. Every little bit matters. You can and do make a difference.',
    descChinese: '慷慨为怀。把自己的时间、精力和财力用于改善社区和其他人的生活。涓涓细流汇成大河。你能够做到，也的确能够做到。' },
  { id: 9, name: 'Get clear on expectations', nameChinese: '明确要求',
    desc: 'Create clarity and avoid misunderstandings by discussing expectations upfront. Set expectations for others and ask when you\'re not clear on what they expect of you. End all meetings with clarity about action items, responsibilities, and due dates.',
    descChinese: '事先讨论各项要求，明确认识，避免误解。对其他人提出期望，你不清楚别人对你的期望时，应该提问。每次会议结束时都要阐明事项、职责和截止期限。' },
  { id: 10, name: 'Listen generously', nameChinese: '认真倾听',
    desc: 'Listening is more than simply "not speaking." Give others your undivided attention. Be present and engaged. Minimize the distractions and let go of the need to agree or disagree. Check your biases and be curious to know more, rather than jumping to conclusions. Above all, listen to understand.',
    descChinese: '听并非只是"不说话"而已。而是把全部注意力放在对方身上。全神贯注。把干扰降到最低，倾听时不要纠结于该支持还是该反对对方的观点。克制自己的偏见，渴望了解更多信息，而不急于下结论。更重要的是，倾听以了解他人。' },
  { id: 11, name: 'Speak straight', nameChinese: '坦诚直言',
    desc: 'Speak honestly in a way that helps to make progress. Say what you mean, and be willing to ask questions, share ideas, or raise issues that may cause conflict when it\'s necessary for team success. Be courageous enough to say what needs to be said. Address issues directly with those who are involved or affected.',
    descChinese: '以有助于工作进展的方式坦诚直言。如实表达自己的意思，在对团队成功必要时，愿意提问、分享看法或提出可能会引起冲突的问题。勇于说出非说不可的话。直接与当事人或受影响的人一起解决问题。' },
  { id: 12, name: 'Think team first', nameChinese: '优先思考团队',
    desc: 'Don\'t let your own ego or personal agenda get in the way of doing what\'s best for the team. Consider how what you do impacts others. Make smart decisions that enhance long-term relationships and build trust. Be there for each other and be willing to step into another role or help a co-worker when that\'s what\'s needed.',
    descChinese: '不要让自己的自尊心或个人意图阻碍团队的利益。考虑自己的行为会如何影响他人。作出明智的决定，以利于增进长期关系并建立信任。互相帮助，并愿意担负其他角色，或帮助同事，促进成功。' },
  { id: 13, name: 'Compete to win', nameChinese: '竞争制胜',
    desc: 'While we appreciate effort, we reward and celebrate results. Never quit. Be scrappy. Set high goals, use measurements to track your progress, and hold yourself accountable for achieving those results. Win the right way.',
    descChinese: '我们重视努力，也奖励和庆祝成就。永不言退。斗志昂扬。树立高目标，用各种衡量标准跟踪进度，并对取得成果担负起责任。赢之有道。' },
  { id: 14, name: 'Take ownership', nameChinese: '发挥主人翁精神',
    desc: 'Be a problem solver and take personal responsibility for making things happen. Be calm under pressure and respond to every situation by looking for how we can do it, rather than explaining why it can\'t be done.',
    descChinese: '做一个解决问题的人，为了成事而负起个人责任。面对压力保持冷静，应对各种情况，思考如何能够做到，而不是解释为什么做不到。' },
  { id: 15, name: 'Be process-driven', nameChinese: '以流程为导向',
    desc: 'Create systems and processes that are scalable and that support our ability to perform with consistency. Strong processes are the foundation of organizational effectiveness. They help us to create speed and optimization, rather than chaos.',
    descChinese: '创建可以扩展的系统和流程，从而为我们一以贯之地执行任务提供支持。强大的流程是组织成效的基础，有助于促成速度和优化，而非混乱。' },
  { id: 16, name: 'Assume positive intent', nameChinese: '用善意的眼光看待他人',
    desc: 'Work from the assumption that people are good, fair, and honest, and the intent behind their actions is positive. Set aside your own judgments and preconceived notions. Give people the benefit of the doubt.',
    descChinese: '工作时假定他人是好的、公平的、诚实的，其行动背后的意图是善意的。不要评判他人，放弃先入为主的想法。在没有真凭实据之前，不要怀疑他人。' },
  { id: 17, name: 'Be relentless about improvement', nameChinese: '坚持不懈地追求进步',
    desc: 'Regularly reevaluate every aspect of your job and yourself to find ways to improve. Don\'t be satisfied with the status quo. Guard against complacency and look for opportunities to gain more knowledge, increase your skills, and innovate. Share best practices and find ways to get things done better, faster, and more efficiently.',
    descChinese: '定期重新评估工作的每个方面，设法改进。不满足于现状。警惕自满，寻找机会获得更多的知识，提高自身技能去创新。分享最佳实践，想方设法把事情做得更好，更快，更高效。' },
  { id: 18, name: 'Show meaningful appreciation', nameChinese: '注重真诚的表扬',
    desc: 'Recognizing people doing things right is more effective than pointing out when they do things wrong. Regularly extend meaningful acknowledgment and appreciation — in all directions throughout our organization.',
    descChinese: '表扬他人行事得当的效果要好于指出他人错误。经常真诚地认可及感谢公司各部门的员工。' },
  { id: 19, name: 'Think and act like an owner', nameChinese: '倡导主人翁思维和行为',
    desc: 'Make decisions by asking yourself, "What would I do if this were my company? What would I do if this were my own money? Will this help the company to succeed?"',
    descChinese: '决策时自问："如果这是我自己的公司，我会怎么做？如果这是我自己的钱，我会怎么做？这会有助于公司成功吗？"' },
  { id: 20, name: 'Be positive & encouraging', nameChinese: '正向鼓励',
    desc: 'You have the power to choose your attitude. Choose to be joyful, optimistic, and enthusiastic. Your attitude is contagious. Use it to spread optimism, positive energy and encouragement, just like our core brand Ingenuity, encourage others every step of the way with positivity.',
    descChinese: '你有能力选择自己的态度。选择快乐、乐观和热情。你的态度具有感染力。用它来传播乐观、正能量和鼓励他人。就像我们的核心品牌"Ingenuity"一样，用正向的态度鼓励他人前进的每一步。' },
  { id: 21, name: 'Collaborate', nameChinese: '合作',
    desc: 'Work together. Collaborate internally and with our customers, consumers, and partners to find better solutions. Be open to new ideas and different approaches. Use all our available resources.',
    descChinese: '共同努力。在内部开展合作，并与我们的客户、消费者以及合作伙伴携手，寻找更好的解决方案。对新创意和不同方法保持开放态度。利用我们所有可用的资源。' },
  { id: 22, name: 'Pay attention to the details', nameChinese: '注重细节',
    desc: 'Be thorough and don\'t skip steps. Missing just one detail can have an enormous impact. Be a fanatic about accuracy and precision. The goal is to get things done right and safely, not simply to get them done. Double-check your work. Do it right the first time.',
    descChinese: '做事要周全，不要省略任何的步骤。哪怕只是错过一个细节，都有可能产生重大影响。要对准确和精确保持执着。目标是把事情做对，而不仅仅是做完。对工作进行复查。第一次就做对。' },
  { id: 23, name: 'Be inclusive', nameChinese: '心存包容',
    desc: 'Foster an environment where belonging and inclusion are instinctive and ingrained. Accept and empower individuals to be their authentic selves. Be eager to learn from others and seek different points of view. We make better decisions and deliver better results when we encourage and accept unique experiences and perspectives.',
    descChinese: '创建一个多样性和包容性都出于本能且根深蒂固的环境。接受并允许每个人做真实的自己。渴望向他人学习并寻求不同的观点。当我们鼓励并接受独特的经验和观点时，我们会做出更好的决策并获得更好的结果。' },
  { id: 24, name: 'Be a brand ambassador', nameChinese: '成为品牌大使',
    desc: 'We\'re all responsible for, and benefit from, the Kids2 image and reputation. Consider how your actions affect our collective reputation, and be a proud ambassador for the company.',
    descChinese: '我们每个人都要对Kids2的形象和声誉负责，也都会从中受益。思考你的行为如何影响我们的集体声誉，并成为值得骄傲的公司大使。' },
  { id: 25, name: 'Own your work-life balance', nameChinese: '平衡工作和生活',
    desc: 'Own and protect your work-life balance. Know and communicate boundaries and when you need to take a break to refresh and recharge. To give your best, you must be at your best. Be a steward of our most valuable resource: you!',
    descChinese: '达到并维护好你的工作和生活的平衡状态。了解并与他人沟通自己的底线，清楚自己何时需要一些休息时间给自己提提神充充电。要想展现自己最好的一面，你必须保持最佳的状态。成为我们最宝贵资源的管理人，这个最宝贵的资源，就是你！' },
  { id: 26, name: 'Embrace change', nameChinese: '欢迎变革',
    desc: 'What got us here is not the same as what will get us to the next level. Get outside your comfort zone, rather than stubbornly hanging on to old ways of doing things. Be excited by the possibilities that change brings. Be agile and flexible.',
    descChinese: '过去的成功经验不能保证我们更上一层楼。走出舒适地带，而不固步自封。为变化所带来的各种可能欢欣鼓舞。机敏灵活。' },
  { id: 27, name: "Walk in your customers' shoes", nameChinese: '想客户所想',
    desc: 'Whether it\'s your customer or the consumer, understand their world. Know their challenges and frustrations. See the world from their perspective. The better you understand them, the more effectively you can anticipate and meet their needs. This includes both external and internal customers.',
    descChinese: '了解客户和消费者的内心世界。明白他们所面临的挑战和挫折。从他们的视角看问题。对他们的了解越深，就越能卓有成效地预知并满足其需求。这包括外部和内部客户。' },
  { id: 28, name: 'Share information', nameChinese: '分享信息',
    desc: 'With appropriate respect for confidentiality, share information freely throughout our organization. The more people know, the better we can collaborate. Learn to ask yourself, "Who else needs to know this?"',
    descChinese: '在适当重视保密的前提下，在组织上下自由分享信息。员工知道的越多，越能更好地合作。学会自问："还有谁需要知道这些情况？"' },
  { id: 29, name: 'Look ahead and anticipate', nameChinese: '未雨绸缪',
    desc: 'Solve problems before they happen by anticipating future issues, planning for contingencies, and addressing them in advance. Be curious and ask questions today to ensure an extraordinary tomorrow, embrace our Baby Einstein brand mentality. Preventing issues is always better than fixing them.',
    descChinese: '未雨绸缪，防患于未然，制定应急计划，提前解决问题。纳用我们"Baby Einstein"品牌的思维方式，保持好奇心，今天就提出问题，以确保明天的非凡表现。凡事预留时间提前准备。预防问题永远胜于解决问题。' },
  { id: 30, name: 'Keep things fun', nameChinese: '保持乐趣',
    desc: 'While our passion for excellence is real, remember the world has bigger problems than the daily challenges that make up our work. Stuff happens. Keep perspective and find joy in every moment, just like our Bright Starts brand. Don\'t take things personally or take yourself too seriously. Bring confetti to the chaos and see the humor in life.... laugh every day.',
    descChinese: '虽然我们热诚追求卓越，但请记住，世界问题多多，远不止我们工作中遇到的日常挑战。世事难料。保持清醒的头脑，在每一刻都找到快乐，就像我们的"Bright Start"品牌一样。不要把事情放在心上，也不要把自己看得太重。给生活添加五彩缤纷的颜色，让生活充满幽默....让自己每天开怀大笑。' },
];

// Fundamental of the Week schedule.
// Each entry is the Sunday a fundamental takes effect; it stays active until the
// next Sunday's entry. The order is set by the published calendar — it is NOT
// sequential. To extend, append the next Sunday + fundamental id (keep it sorted
// by date). The schedule holds on the last entry once it runs out.
const FOW_SCHEDULE = [
  { date: '2026-06-07', id: 21 }, // Collaborate
  { date: '2026-06-14', id: 30 }, // Keep things fun
  { date: '2026-06-21', id: 10 }, // Listen generously
  { date: '2026-06-28', id: 12 }, // Think team first
  { date: '2026-07-05', id: 11 }, // Speak straight
  { date: '2026-07-12', id: 8 },  // Make a difference
  { date: '2026-07-19', id: 4 },  // Think globally
  { date: '2026-07-26', id: 3 },  // Demonstrate a design mentality
  { date: '2026-08-02', id: 16 }, // Assume positive intent
  { date: '2026-08-09', id: 6 },  // Honor commitments
  { date: '2026-08-16', id: 13 }, // Compete to win
  { date: '2026-08-23', id: 15 }, // Be process-driven
  { date: '2026-08-31', id: 23 }, // Be inclusive
  { date: '2026-09-07', id: 9 },  // Get clear on expectations
  { date: '2026-09-14', id: 18 }, // Show meaningful appreciation
  { date: '2026-09-21', id: 7 },  // Practice blameless problem-solving
  { date: '2026-09-28', id: 14 }, // Take ownership
  { date: '2026-10-05', id: 5 },  // Be proud of your work
];

function getFundamentalOfTheWeek() {
  const today = new Date().toISOString().split('T')[0];

  // The active fundamental is the latest scheduled entry whose Sunday has arrived.
  // Past the end of the schedule it holds on the last entry; before the schedule
  // starts it shows the first entry. No sequential auto-cycling.
  let matched = FOW_SCHEDULE[0];
  for (let i = FOW_SCHEDULE.length - 1; i >= 0; i--) {
    if (today >= FOW_SCHEDULE[i].date) {
      matched = FOW_SCHEDULE[i];
      break;
    }
  }

  return FUNDAMENTALS.find(f => f.id === matched.id) || FUNDAMENTALS[0];
}

// Group recognitions with same group_id into one entry with multiple receivers
function groupRecognitions(recognitions) {
  const grouped = [];
  const seen = new Set();

  for (const rec of recognitions) {
    if (rec.group_id) {
      if (seen.has(rec.group_id)) continue;
      seen.add(rec.group_id);
      const siblings = recognitions.filter(r => r.group_id === rec.group_id);
      grouped.push({
        ...rec,
        receivers: siblings.map(r => r.receiver),
        isGroup: true,
      });
    } else {
      grouped.push({ ...rec, receivers: [rec.receiver], isGroup: false });
    }
  }
  return grouped;
}

function Feed({ user, onUserUpdate }) {
  const [recognitions, setRecognitions] = useState([]);
  const [teamRecognitions, setTeamRecognitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreTeam, setHasMoreTeam] = useState(true);
  const [offset, setOffset] = useState(0);
  const [teamOffset, setTeamOffset] = useState(0);
  const [activeTab, setActiveTab] = useState('recent');
  const { language } = useLanguage();

  const LIMIT = 20;

  const loadRecognitions = useCallback(async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      const response = await getRecognitions({ limit: LIMIT, offset: currentOffset });

      if (reset) {
        setRecognitions(response.data);
        setOffset(LIMIT);
      } else {
        setRecognitions(prev => [...prev, ...response.data]);
        setOffset(prev => prev + LIMIT);
      }

      setHasMore(response.data.length === LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  const loadTeamRecognitions = useCallback(async (reset = false) => {
    if (!user.is_manager) return;

    setTeamLoading(true);
    try {
      const currentOffset = reset ? 0 : teamOffset;
      const response = await getRecognitions({ limit: LIMIT, offset: currentOffset, team: true });

      if (reset) {
        setTeamRecognitions(response.data);
        setTeamOffset(LIMIT);
      } else {
        setTeamRecognitions(prev => [...prev, ...response.data]);
        setTeamOffset(prev => prev + LIMIT);
      }

      setHasMoreTeam(response.data.length === LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setTeamLoading(false);
    }
  }, [teamOffset, user.is_manager]);

  useEffect(() => {
    loadRecognitions(true);
  }, []);

  useEffect(() => {
    if (user.is_manager && activeTab === 'team' && teamRecognitions.length === 0) {
      loadTeamRecognitions(true);
    }
  }, [activeTab, user.is_manager]);

  function handleRecognitionCreated() {
    loadRecognitions(true);
    if (user.is_manager) {
      loadTeamRecognitions(true);
    }
    onUserUpdate();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const fow = getFundamentalOfTheWeek();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Main Content */}
      <div className="lg:col-span-8 space-y-6">
        {/* Fundamental of the Week */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 via-amber-50/50 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600 flex-shrink-0">
                <Star className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">
                  {language === 'zh' ? '本周基本原则' : 'Fundamental of the Week'}
                </p>
                <p className="text-sm font-medium text-foreground">
                  #{fow.id} – {language === 'zh' ? fow.nameChinese : fow.name}
                </p>
              </div>
            </div>
            {fow.desc && (
              <p className="text-sm text-muted-foreground ml-14">
                {language === 'zh' ? fow.descChinese : fow.desc}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recognition CTA Card */}
        <Card className="border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-1">
                {t('recognizeColleague', language)}
              </h2>
              <p className="text-muted-foreground">
                {language === 'zh' ? '表彰同事的出色表现' : 'Celebrate a colleague\'s great work'}
              </p>
            </div>
            <RecognitionModal
              currentUser={user}
              onSuccess={handleRecognitionCreated}
              trigger={
                <Button
                  size="lg"
                  className="gap-2 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <Sparkles className="h-4 w-4" />
                  {t('giveRecognition', language)}
                </Button>
              }
            />
          </CardContent>
        </Card>

        {/* Recognition Feed */}
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="recent" className="gap-2">
                <Clock className="h-4 w-4" />
                {t('recent', language)}
              </TabsTrigger>
              <TabsTrigger value="popular" className="gap-2">
                <TrendingUp className="h-4 w-4" />
                {t('popular', language)}
              </TabsTrigger>
              {user.is_manager && (
                <TabsTrigger value="team" className="gap-2">
                  <Users className="h-4 w-4" />
                  {t('myTeam', language)}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="recent" className="space-y-4">
              {error && (
                <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {error}
                </div>
              )}

              {recognitions.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t('noRecognitionsYet', language)}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t('beFirstToRecognize', language)}
                    </p>
                    <RecognitionModal
                      currentUser={user}
                      onSuccess={handleRecognitionCreated}
                    />
                  </CardContent>
                </Card>
              ) : (
                groupRecognitions(recognitions).map(recognition => (
                  <RecognitionCard
                    key={recognition.id}
                    recognition={recognition}
                    currentUser={user}
                    onChange={handleRecognitionCreated}
                  />
                ))
              )}

              {hasMore && recognitions.length > 0 && (
                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    onClick={() => loadRecognitions(false)}
                  >
                    {t('loadMore', language)}
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="popular" className="space-y-4">
              {groupRecognitions([...recognitions]
                .sort((a, b) => {
                  const getScore = (rec) => {
                    const likes = rec.like_count || 0;
                    const comments = rec.comment_count || 0;
                    const engagement = likes + comments * 2;
                    const ageDays = (Date.now() - new Date(rec.created_at).getTime()) / 86400000;
                    return engagement - (ageDays / 7);
                  };
                  return getScore(b) - getScore(a);
                }))
                .map(recognition => (
                  <RecognitionCard
                    key={recognition.id}
                    recognition={recognition}
                    currentUser={user}
                    onChange={handleRecognitionCreated}
                  />
                ))
              }
            </TabsContent>

            {user.is_manager && (
              <TabsContent value="team" className="space-y-4">
                {teamLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : teamRecognitions.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        {t('noTeamRecognitions', language)}
                      </h3>
                      <p className="text-muted-foreground">
                        {t('noTeamRecognitionsDesc', language)}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {groupRecognitions(teamRecognitions).map(recognition => (
                      <RecognitionCard
                        key={recognition.id}
                        recognition={recognition}
                        currentUser={user}
                        onChange={handleRecognitionCreated}
                      />
                    ))}
                    {hasMoreTeam && (
                      <div className="flex justify-center pt-4">
                        <Button
                          variant="outline"
                          onClick={() => loadTeamRecognitions(false)}
                        >
                          {t('loadMore', language)}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-4 hidden lg:block">
        <ActivitySidebar user={user} />
      </div>
    </div>
  );
}

export default Feed;
